import React, { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, TrendingUp, TrendingDown, Zap } from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { AssetHolding } from '../types';
import { Money } from './ui/SensitiveValue';
import { v4 } from '../utils/uuid';
import { fetchAllPrices } from '../utils/priceUpdateService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_HOLDING = (assetId: string): Omit<AssetHolding, 'id'> => ({
  assetId,
  symbol:      '',
  name:        '',
  quantity:    0,
  costPrice:   0,
  marketPrice: 0,
  provider:    'manual',
  lastUpdated: new Date().toISOString(),
});

function formatQty(n: number): string {
  if (Number.isInteger(n)) return n.toLocaleString('vi-VN');
  return n.toLocaleString('vi-VN', { maximumFractionDigits: 4 });
}

function formatPct(n: number): string {
  const s = (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
  return s;
}

// ── Form Modal ────────────────────────────────────────────────────────────────

interface FormProps {
  assetId:  string;
  initial?: AssetHolding;
  onClose:  () => void;
}

function HoldingForm({ assetId, initial, onClose }: FormProps) {
  const { dispatch } = useAppState();
  const [symbol,      setSymbol]      = useState(initial?.symbol      ?? '');
  const [name,        setName]        = useState(initial?.name        ?? '');
  const [quantity,    setQuantity]    = useState(initial?.quantity    != null ? String(initial.quantity)    : '');
  const [costPrice,   setCostPrice]   = useState(initial?.costPrice   != null ? String(initial.costPrice)   : '');
  const [marketPrice, setMarketPrice] = useState(initial?.marketPrice != null ? String(initial.marketPrice) : '');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbol.trim()) { setError('Vui lòng nhập mã giao dịch.'); return; }
    const qty  = parseFloat(quantity.replace(/,/g, ''));
    const cost = parseFloat(costPrice.replace(/,/g, ''));
    const mkt  = parseFloat(marketPrice.replace(/,/g, '')) || cost;
    if (!qty  || qty  < 0) { setError('Số lượng không hợp lệ.'); return; }
    if (!cost || cost < 0) { setError('Giá vốn không hợp lệ.'); return; }

    const holding: AssetHolding = {
      id:          initial?.id ?? v4(),
      assetId,
      symbol:      symbol.trim().toUpperCase(),
      name:        name.trim() || symbol.trim().toUpperCase(),
      quantity:    qty,
      costPrice:   cost,
      marketPrice: mkt,
      provider:    initial?.provider ?? 'manual',
      lastUpdated: new Date().toISOString(),
    };

    dispatch({ type: initial ? 'UPDATE_HOLDING' : 'ADD_HOLDING', payload: holding });
    onClose();
  };

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--bd)',
    borderRadius: 8, padding: '9px 12px', fontSize: 13, color: 'var(--tx-1)',
    width: '100%', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--tx-4)', marginBottom: 5, display: 'block',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--bd)', borderRadius: 16,
        padding: '28px 28px 24px', width: '100%', maxWidth: 420,
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-1)', marginBottom: 20 }}>
          {initial ? 'Chỉnh sửa vị thế' : 'Thêm vị thế mới'}
        </div>

        {error && (
          <div style={{
            marginBottom: 16, padding: '9px 14px', borderRadius: 8,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
            fontSize: 12, color: '#f87171',
          }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Mã giao dịch *</label>
              <input style={inputStyle} value={symbol}
                onChange={e => { setSymbol(e.target.value.toUpperCase()); setError(''); }}
                placeholder="VD: VNM, BTC, HPG" autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Tên hiển thị</label>
              <input style={inputStyle} value={name}
                onChange={e => setName(e.target.value)}
                placeholder="VD: Vinamilk, Bitcoin" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Số lượng *</label>
              <input style={inputStyle} type="number" min="0" step="any"
                value={quantity} onChange={e => setQuantity(e.target.value)}
                placeholder="VD: 1000" />
            </div>
            <div>
              <label style={labelStyle}>Giá vốn (₫) *</label>
              <input style={inputStyle} type="number" min="0" step="any"
                value={costPrice} onChange={e => setCostPrice(e.target.value)}
                placeholder="VD: 80000" />
            </div>
            <div>
              <label style={labelStyle}>Giá thị trường (₫)</label>
              <input style={inputStyle} type="number" min="0" step="any"
                value={marketPrice} onChange={e => setMarketPrice(e.target.value)}
                placeholder="Mặc định = giá vốn" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              padding: '9px 18px', borderRadius: 9, border: '1px solid var(--bd)',
              background: 'transparent', color: 'var(--tx-3)', cursor: 'pointer', fontSize: 13,
            }}>
              Huỷ
            </button>
            <button type="submit" style={{
              padding: '9px 20px', borderRadius: 9, border: 'none',
              background: 'linear-gradient(135deg,#00b4db,#00D1FF)',
              color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>
              {initial ? 'Lưu thay đổi' : 'Thêm vị thế'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  assetId:         string;
  onRefreshPrices: () => Promise<void>;
  refreshing?:     boolean;
}

export function HoldingsTable({ assetId, onRefreshPrices, refreshing = false }: Props) {
  const { state, dispatch } = useAppState();
  const [addOpen,       setAddOpen]       = useState(false);
  const [editTarget,    setEditTarget]    = useState<AssetHolding | null>(null);
  const [refreshingId,  setRefreshingId]  = useState<string | null>(null);  // holdingId being single-refreshed

  const holdings = useMemo(
    () => (state.holdings || []).filter(h => h.assetId === assetId),
    [state.holdings, assetId]
  );

  const handleRefreshSingle = async (h: AssetHolding) => {
    setRefreshingId(h.id);
    try {
      // Pass a synthetic asset list with just this symbol so the price service can classify it
      const syntheticAssets = [{ id: h.id, code: h.symbol, name: h.name } as any];
      const results = await fetchAllPrices(syntheticAssets, () => {});
      const match = results.find(r => r.code.toUpperCase() === h.symbol.toUpperCase());
      if (match) {
        dispatch({
          type: 'UPDATE_HOLDING',
          payload: { ...h, marketPrice: match.price, provider: match.source, lastUpdated: match.fetchedAt },
        });
      }
    } finally {
      setRefreshingId(null);
    }
  };

  const totalValue = useMemo(
    () => holdings.reduce((s, h) => s + h.quantity * h.marketPrice, 0),
    [holdings]
  );

  const totalCost = useMemo(
    () => holdings.reduce((s, h) => s + h.quantity * h.costPrice, 0),
    [holdings]
  );

  const handleDelete = (h: AssetHolding) => {
    if (window.confirm(`Xóa vị thế "${h.symbol}" (${formatQty(h.quantity)} đơn vị)?`))
      dispatch({ type: 'DELETE_HOLDING', payload: h.id });
  };

  const th: React.CSSProperties = {
    padding: '7px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--tx-5)', whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--bd)',
  };
  const td: React.CSSProperties = {
    padding: '10px 12px', fontSize: 12, color: 'var(--tx-2)', borderBottom: '1px solid var(--bd)',
  };

  return (
    <div style={{ marginTop: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent, #00D1FF)' }}>
          Quản lý vị thế ({holdings.length})
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onRefreshPrices} disabled={refreshing} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(16,185,129,0.3)',
            background: 'rgba(16,185,129,0.07)', color: '#10b981', cursor: refreshing ? 'not-allowed' : 'pointer',
            fontSize: 11, fontWeight: 600, opacity: refreshing ? 0.6 : 1,
          }}>
            <RefreshCw size={12} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            Cập nhật tất cả
          </button>
          <button onClick={() => setAddOpen(true)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 8, border: 'none',
            background: 'rgba(0,209,255,0.12)', color: '#00D1FF', cursor: 'pointer', fontSize: 11, fontWeight: 600,
          }}>
            <Plus size={12} /> Thêm vị thế
          </button>
        </div>
      </div>

      {holdings.length === 0 ? (
        <div style={{
          padding: '24px', textAlign: 'center', borderRadius: 12,
          border: '1px dashed var(--bd)', color: 'var(--tx-5)', fontSize: 12,
        }}>
          Chưa có vị thế nào. Nhấn "Thêm vị thế" để bắt đầu.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--bd)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)' }}>
                <th style={{ ...th, textAlign: 'left' }}>Mã</th>
                <th style={{ ...th, textAlign: 'left' }}>Tên</th>
                <th style={{ ...th, textAlign: 'right' }}>Số lượng</th>
                <th style={{ ...th, textAlign: 'right' }}>Giá vốn</th>
                <th style={{ ...th, textAlign: 'right' }}>Giá TT</th>
                <th style={{ ...th, textAlign: 'right' }}>Giá trị TT</th>
                <th style={{ ...th, textAlign: 'right' }}>Lãi/Lỗ</th>
                <th style={{ ...th, textAlign: 'right' }}>Tỷ trọng</th>
                <th style={{ ...th, textAlign: 'center' }}>Nguồn</th>
                <th style={{ ...th, textAlign: 'center' }}>Thị giá</th>
                <th style={{ ...th, textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {holdings.map(h => {
                const mktValue = h.quantity * h.marketPrice;
                const costValue = h.quantity * h.costPrice;
                const pnl = mktValue - costValue;
                const pnlPct = costValue > 0 ? (pnl / costValue) * 100 : 0;
                const weight = totalValue > 0 ? (mktValue / totalValue) * 100 : 0;
                const isUp = pnl >= 0;

                return (
                  <tr key={h.id} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(255,255,255,0.025)'}
                    onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'}
                  >
                    <td style={{ ...td, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '0.05em' }}>
                      {h.symbol}
                    </td>
                    <td style={{ ...td, color: 'var(--tx-3)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.name || '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatQty(h.quantity)}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <Money value={h.costPrice}  />
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <Money value={h.marketPrice}  />
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      <Money value={mktValue} />
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                        {isUp
                          ? <TrendingUp size={11} style={{ color: '#10b981', flexShrink: 0 }} />
                          : <TrendingDown size={11} style={{ color: '#f87171', flexShrink: 0 }} />}
                        <span style={{ color: isUp ? '#10b981' : '#f87171', fontWeight: 600 }}>
                          <Money value={Math.abs(pnl)} prefix={pnl >= 0 ? '+' : '-'} />
                        </span>
                        <span style={{ color: isUp ? '#10b981' : '#f87171', fontSize: 10 }}>
                          ({formatPct(pnlPct)})
                        </span>
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--tx-4)' }}>
                      {weight.toFixed(1)}%
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 99,
                        background: 'rgba(255,255,255,0.06)', fontSize: 9, fontWeight: 700,
                        color: 'var(--tx-5)', letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>
                        {h.provider}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button
                        onClick={() => handleRefreshSingle(h)}
                        disabled={refreshingId === h.id}
                        title={`Cập nhật thị giá ${h.symbol}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '4px 9px', borderRadius: 7,
                          border: '1px solid rgba(56,189,248,0.3)',
                          background: refreshingId === h.id ? 'rgba(56,189,248,0.04)' : 'rgba(56,189,248,0.08)',
                          color: '#38bdf8', cursor: refreshingId === h.id ? 'not-allowed' : 'pointer',
                          fontSize: 10, fontWeight: 600, opacity: refreshingId === h.id ? 0.6 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Zap size={10} style={refreshingId === h.id ? { animation: 'spin 0.8s linear infinite' } : undefined} />
                        {refreshingId === h.id ? '...' : 'Cập nhật'}
                      </button>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button onClick={() => setEditTarget(h)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--tx-5)', padding: 4, borderRadius: 6,
                          transition: 'color 0.15s',
                        }}
                          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-2)'}
                          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-5)'}
                        >
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(h)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--tx-5)', padding: 4, borderRadius: 6,
                          transition: 'color 0.15s',
                        }}
                          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = '#f87171'}
                          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-5)'}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Footer tổng */}
            <tfoot>
              <tr style={{ background: 'rgba(255,255,255,0.025)' }}>
                <td colSpan={5} style={{ ...td, fontWeight: 700, color: 'var(--tx-3)', borderTop: '1px solid var(--bd)', borderBottom: 'none' }}>
                  Tổng cộng ({holdings.length} vị thế)
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--tx-1)', borderTop: '1px solid var(--bd)', borderBottom: 'none' }}>
                  <Money value={totalValue}  />
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, borderTop: '1px solid var(--bd)', borderBottom: 'none' }}>
                  {(() => {
                    const totalPnl = totalValue - totalCost;
                    const totalPnlPct = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;
                    return (
                      <span style={{ color: totalPnl >= 0 ? '#10b981' : '#f87171' }}>
                        <Money value={Math.abs(totalPnl)} prefix={totalPnl >= 0 ? '+' : '-'} />
                        <span style={{ fontSize: 10 }}> ({formatPct(totalPnlPct)})</span>
                      </span>
                    );
                  })()}
                </td>
                <td colSpan={4} style={{ borderTop: '1px solid var(--bd)', borderBottom: 'none' }} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {addOpen && (
        <HoldingForm assetId={assetId} onClose={() => setAddOpen(false)} />
      )}
      {editTarget && (
        <HoldingForm assetId={assetId} initial={editTarget} onClose={() => setEditTarget(null)} />
      )}
    </div>
  );
}
